import React, { useState, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Collapse,
    Avatar,
    AvatarGroup,
    Stack,
    Tooltip,
    Divider,
    Modal,
    Snackbar,
    Grid,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    PlayArrow as PlayArrowIcon,
    Close as CloseIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { CircularImage } from './CircularImage';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { formatDuration } from '../utils/time';

interface Entity {
    id: string;
    name: string;
    imageUrl: string;
}

interface Analysis {
    podcast_context: string;
    brand_entry: string;
    narrative: string;
    tone_language: string;
    functional_message: string;
    brand_repetition: string;
    action_element: string;
    audience_fit: string;
    pacing: string;
    transition_quality: string;
    mention_type: string;
}

interface Mention {
    startTime: number;
    endTime: number;
    text: string;
    entity: Entity;
    _id?: string;
    analysis?: Analysis;
    analyzedAt?: Date;
    originalMention?: any;
}

interface EntityMentionsProps {
    mentions: Mention[];
    onTimeClick?: (time: number) => void;
    onAvatarClick?: (mention: Mention) => void;
    showExpandIcon?: boolean;
    showZeroCount?: boolean;
    emptyText?: string;
    showText?: boolean;
    avatarSize?: number;
    maxAvatars?: number;
}

const MentionItem: React.FC<{
    mention: Mention;
    onTimeClick?: (time: number) => void;
    onAvatarClick?: (mention: Mention) => void;
}> = ({ mention, onTimeClick, onAvatarClick }) => {
    const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [showCopiedSnackbar, setShowCopiedSnackbar] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const handlePlayClick = () => {
        if (onTimeClick) {
            onTimeClick(mention.startTime);
        }
    };

    const handleCloseFullscreen = () => {
        setIsVideoFullscreen(false);
        setSelectedImage(null);
        if (videoRef.current) {
            videoRef.current.pause();
        }
    };

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = 200;
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth',
            });
        }
    };

    const handleCopyId = () => {
        if (mention._id) {
            navigator.clipboard.writeText(mention._id);
            setShowCopiedSnackbar(true);
        }
    };

    return (
        <Box sx={{ mb: 2, mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                    src={mention.entity.imageUrl}
                    alt={mention.entity.name}
                    onClick={() => onAvatarClick?.(mention)}
                    sx={{ cursor: onAvatarClick ? 'pointer' : 'default' }}
                />
                <Typography variant="body2">{mention.entity.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {formatDuration(mention.startTime)} - {formatDuration(mention.endTime)}
                </Typography>
                <IconButton size="small" onClick={handlePlayClick} sx={{ ml: 'auto' }}>
                    <PlayArrowIcon fontSize="small" />
                </IconButton>
            </Stack>
            {process.env.NODE_ENV === 'development' && (
                <Box sx={{ ml: 4, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                        onClick={handleCopyId}
                    >
                        {mention._id}
                    </Typography>
                    <Tooltip title="Copy ID">
                        <IconButton size="small" onClick={handleCopyId}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
            <Typography sx={{ ml: 4, display: 'block', mb: 1 }}>{mention.text}</Typography>

            {mention.analysis && (
                <Box sx={{ ml: 4 }}>
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Análisis Cualitativo
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Contexto del Podcast
                                </Typography>
                                <Typography variant="body2">
                                    {mention.analysis.podcast_context}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Entrada de la Marca
                                </Typography>
                                <Typography variant="body2">
                                    {mention.analysis.brand_entry}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Narrativa
                                </Typography>
                                <Typography variant="body2">
                                    {mention.analysis.narrative}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Tono y Lenguaje
                                </Typography>
                                <Typography variant="body2">
                                    {mention.analysis.tone_language}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Mensaje Funcional
                                </Typography>
                                <Typography variant="body2">
                                    {mention.analysis.functional_message}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Repetición de Marca
                                </Typography>
                                <Typography variant="body2">
                                    {mention.analysis.brand_repetition}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Elemento de Acción
                                </Typography>
                                <Typography variant="body2">
                                    {mention.analysis.action_element}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Ajuste con la Audiencia
                                </Typography>
                                <Typography variant="body2">
                                    {mention.analysis.audience_fit}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Ritmo
                                </Typography>
                                <Typography variant="body2">{mention.analysis.pacing}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Calidad de Transición
                                </Typography>
                                <Typography variant="body2">
                                    {mention.analysis.transition_quality}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Tipo de Mención
                                </Typography>
                                <Typography variant="body2">
                                    {mention.analysis.mention_type}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            )}


            <Modal
                open={isVideoFullscreen || !!selectedImage}
                onClose={handleCloseFullscreen}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0, 0, 0, 0.9)',
                }}
            >
                <Box sx={{ width: '90vw', maxWidth: 1200, position: 'relative' }}>
                    <IconButton
                        onClick={handleCloseFullscreen}
                        sx={{
                            position: 'absolute',
                            top: -40,
                            right: 0,
                            color: 'white',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <img
                        src={selectedImage || ''}
                        alt="Full size frame"
                        style={{
                            width: '100%',
                            maxHeight: '90vh',
                            objectFit: 'contain',
                            borderRadius: 10,
                        }}
                    />
                </Box>
            </Modal>
            <Snackbar
                open={showCopiedSnackbar}
                autoHideDuration={2000}
                onClose={() => setShowCopiedSnackbar(false)}
                message="ID copied to clipboard"
            />
        </Box>
    );
};

export const EntityMentions: React.FC<EntityMentionsProps> = ({
    mentions,
    onTimeClick,
    onAvatarClick,
    showExpandIcon = true,
    showZeroCount = true,
    emptyText = 'No mentions',
    showText = true,
    avatarSize = 40,
    maxAvatars = 5,
}) => {
    const [expanded, setExpanded] = useState(false);
    const uniqueEntities = useMemo(() => {
        const seen = new Set<string>();
        return mentions.filter(mention => {
            const key = mention.entity.id;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [mentions]);

    const text = useMemo(() => {
        if (uniqueEntities.length === 0) {
            return emptyText;
        }
        if (!showText) {
            return '';
        }
        if (uniqueEntities.length <= 2) {
            return uniqueEntities.map(m => m.entity.name).join(', ');
        }
        return `${uniqueEntities
            .slice(0, 1)
            .map(m => m.entity.name)
            .join(', ')} y más`;
    }, [uniqueEntities, showText, emptyText]);

    if (uniqueEntities.length === 0 && !showZeroCount) {
        return null;
    }

    return (
        <Box
            sx={{
                height: '100%',
                alignItems: 'center',
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center">
                {uniqueEntities.length > 0 && (
                    <AvatarGroup
                        max={maxAvatars}
                        renderSurplus={surplus => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    pl: 0.6,
                                    width: '20px',
                                }}
                            >
                                <Typography variant="body2" sx={{ fontSize: 8 }}>
                                    +{surplus}
                                </Typography>
                            </Box>
                        )}
                        sx={{
                            '& .MuiAvatar-root': {
                                width: avatarSize,
                                height: avatarSize,
                                fontSize: avatarSize * 0.6,
                            },
                        }}
                    >
                        {uniqueEntities.map(mention => (
                            <Tooltip key={mention.entity.id} title={mention.entity.name}>
                                <Avatar
                                    onClick={() => onAvatarClick?.(mention)}
                                    sx={{
                                        width: avatarSize,
                                        height: avatarSize,
                                        p: 0,
                                        border: 'none',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        transition:
                                            'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                        '&:hover': {
                                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                            cursor: onAvatarClick ? 'pointer' : 'default',
                                        },
                                    }}
                                >
                                    <CircularImage
                                        src={mention.entity.imageUrl}
                                        alt={mention.entity.name}
                                        size={avatarSize}
                                    />
                                </Avatar>
                            </Tooltip>
                        ))}
                    </AvatarGroup>
                )}
                {text && <Typography variant="body2">{text}</Typography>}
                {showExpandIcon && uniqueEntities.length > 0 && (
                    <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ ml: 1 }}>
                        <ExpandMoreIcon
                            sx={{
                                transform: expanded ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s',
                            }}
                        />
                    </IconButton>
                )}
            </Stack>
            {showExpandIcon && (
                <Collapse in={expanded}>
                    <Box sx={{ mt: 1 }}>
                        <Divider />
                        {mentions.map(mention => (
                            <MentionItem
                                key={mention._id || `${mention.startTime}-${mention.endTime}`}
                                mention={mention}
                                onTimeClick={onTimeClick}
                                onAvatarClick={onAvatarClick}
                            />
                        ))}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
};
